/**
 * Liste blanche des mutations éligibles à la file de synchronisation hors ligne
 * (`ARCHITECTURE_V2.md` §2.4, étape 1 du §8).
 *
 * 🔴 **Une mutation entre dans la file parce qu'elle y a été inscrite nommément,
 * jamais par défaut.** C'est toute la raison d'être de ce fichier, et l'inverser
 * — intercepter tout ce qui n'est pas un `GET` — produirait exactement le défaut
 * que le §2.4 décrit : une file qui met en attente « envoyer une notification
 * d'essai » la déclencherait trois heures plus tard, sans que rien ne l'ait
 * demandé. `isSyncable()` répond donc **`false` pour tout ce qu'elle ne
 * reconnaît pas**, y compris une route qui n'existe pas encore.
 *
 * ⚠️ **Rien n'est branché dessus à l'étape 1, et c'est voulu.** La file n'existe
 * pas avant l'étape 3 (§8-3) : ce fichier est le contrat qu'elle consommera, posé
 * d'avance pour qu'il soit écrit une fois, au calme, plutôt qu'improvisé au
 * milieu d'un drainage. Il est en revanche **vérifié** dès maintenant — un banc
 * confronte cette liste à l'inventaire réel des routes du serveur, de sorte
 * qu'une route de mutation ajoutée plus tard sans décision explicite se voie.
 *
 * ⚠️ **Pourquoi dans `shared/` et pas dans `web/src/lib/`.** Ce qu'énumère cette
 * liste, ce sont des **routes serveur**. C'est le serveur qui en ajoute, et c'est
 * en l'ajoutant qu'il faut décider si elle est rejouable. La ranger côté front
 * l'aurait éloignée du code qui la fait bouger. Le §3 de l'archi V1 ne prévoyait
 * que `shared/types.ts` : ce second fichier est un écart assumé, et il évite
 * d'élargir `types.ts` au-delà de son mandat (« du vocabulaire d'API, aucune
 * logique » — la brèche ouverte à l'étape 9 pour `roundMacroForDisplay` ne doit
 * pas s'agrandir).
 */

/** Les quatre verbes qui écrivent. `GET` ne passe jamais par la file. */
export type SyncableMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Politique de conflit (`ARCHITECTURE_V2.md` §3.4), déclarée **par route**.
 *
 * 🔴 **Déclarée, et non déduite du verbe HTTP.** La tentation est d'écrire
 * « `PUT`/`PATCH` ⇒ dernière écriture gagne, `POST` ⇒ ajout » et de s'en
 * remettre à la méthode. Ça marcherait pour **toutes les routes sauf une**, et
 * se tromperait sur celle-là : `POST /api/days/:date/apply-template`
 * est un `POST` qui **écrase** le snapshot d'une journée. Deux appareils qui
 * appliquent deux modèles différents sont en conflit, et c'est le plus récent
 * qui doit gagner — pas les deux, pas le plus lent.
 *
 * ⚠️ **Aucun contrôle de version optimiste**, et c'est une décision (§3.4). Ces
 * trois politiques sont choisies pour que **le résultat naturel d'un rejeu soit
 * déjà le bon**, ce qui rend la détection de conflit inutile. Ne pas
 * réintroduire de jeton de version : il faudrait ensuite expliquer une
 * « écriture refusée » hors ligne, au milieu d'une séance.
 */
export type ConflictPolicy =
  /**
   * **Ajout — aucun conflit possible.** Deux appareils qui ajoutent produisent
   * deux lignes distinctes, ce qui est le résultat voulu. Une entrée de journal
   * ne remplace rien.
   */
  | 'append'
  /**
   * **La dernière écriture gagne**, départagée par l'horodatage **d'émission
   * cliente** — pas de réception. Sinon la file la plus lente gagnerait, ce qui
   * inverse l'ordre réel des décisions : une pesée saisie ce matin sur un
   * téléphone hors ligne écraserait celle corrigée à midi sur l'ordinateur.
   */
  | 'last-write-wins'
  /**
   * **La suppression l'emporte** sur une modification concurrente. Rejouer une
   * modification sur une ligne supprimée répond 404, que le client traite déjà
   * comme un succès depuis l'étape 5 (RFC 9110 §9.2.2).
   */
  | 'delete-wins';

/**
 * Où lire, dans la réponse, l'`id` que cette création vient d'attribuer.
 *
 * 🔴 **C'est ce qui rend un place-titre `{{op:<seq>}}` résoluble** (§2.3-a).
 * Sans descripteur, une opération dépendante ne peut pas être émise : la file
 * la refuse à la mise en file plutôt que de deviner.
 *
 * ⚠️ **Absent = « cette route ne produit pas d'identité exacte », et c'est un
 * refus délibéré, pas un oubli.** Deux routes de création en sont
 * volontairement privées :
 *
 *   - `POST /api/meals/:id/copy` crée *n* entrées d'un coup, dont aucune n'est
 *     individuellement désignée — son `clientId` est décliné par entrée source
 *     (`<clientId>#<id>`). Il n'y a pas *un* id à renvoyer.
 *   - `POST /api/weight/goals` répond par l'écran de poids, dont le champ
 *     `goal` est **l'objectif courant** — qui peut être celui d'un autre
 *     appareil, arrivé entre-temps. Une résolution capable de désigner la
 *     mauvaise ligne est pire qu'une résolution absente : ici elle
 *     supprimerait un objectif qu'on n'a pas voulu supprimer.
 */
export type CreatedIdSource =
  /** La réponse **est** la ressource créée : son `id` est à la racine. */
  | 'response-id'
  /**
   * La réponse est une **journée** ; retrouver le repas par le `clientId` que
   * la file a elle-même choisi (`MealDTO.clientId`). Jamais « le dernier de la
   * liste » : une devinette qui se trompe écrit dans le mauvais repas.
   */
  | 'day-meal'
  /** Idem pour une entrée de journal (`LogEntryDTO.clientId`). */
  | 'day-entry';

/** Une mutation rejouable, et le mécanisme qui la rend sûre. */
export interface SyncableRoute {
  method: SyncableMethod;
  /** Motif de chemin ; les segments dynamiques portent leur nom (`:id`, `:date`…). */
  pattern: string;
  /**
   * **Pourquoi** le rejeu est sûr — audité route par route au §2.1 de la V2.
   * Ce n'est pas de la documentation d'agrément : inscrire une route sans
   * pouvoir remplir ce champ, c'est constater qu'elle n'est pas éligible.
   */
  idempotence: string;
  /** Comment cette écriture se départage d'une écriture concurrente (§3.4). */
  conflict: ConflictPolicy;
  /** Où lire l'`id` créé, quand cette route en produit un d'exact. */
  creates?: CreatedIdSource;
}

/** Une mutation **hors** file, et le motif du refus. */
export interface NonSyncableRoute {
  /** `'*'` couvre tous les verbes du préfixe. */
  method: SyncableMethod | '*';
  /** Motif de chemin ; un `*` final couvre tout ce qui suit. */
  pattern: string;
  reason: string;
}

/* ==========================================================================
 * La liste blanche
 * ======================================================================= */

/**
 * Les mutations que la file a le droit de mettre en attente.
 *
 * Toutes ont été auditées conformes au §2.1 : création portant un `clientId`,
 * clé naturelle, `PUT`/`PATCH` adressé par la ressource, ou `DELETE`
 * (idempotent sur l'état, RFC 9110 §9.2.2 — un rejeu répond 404, que le client
 * traite déjà comme un succès depuis l'étape 5).
 */
export const SYNCABLE_MUTATIONS: readonly SyncableRoute[] = [
  /* --- Journal (étape 5) ------------------------------------------------ */
  {
    method: 'POST',
    pattern: '/api/days/:date/apply-template',
    conflict: 'last-write-wins',
    idempotence: 'Convergence : rejouer réécrit le même snapshot.',
  },
  {
    method: 'POST',
    pattern: '/api/days/:date/meals',
    conflict: 'append',
    creates: 'day-meal',
    idempotence: '`clientId` unique sur `meal`.',
  },
  {
    method: 'PATCH',
    pattern: '/api/meals/:id',
    conflict: 'last-write-wins',
    idempotence: 'Valeurs absolues, aucun incrément.',
  },
  {
    method: 'DELETE',
    pattern: '/api/meals/:id',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },
  {
    method: 'POST',
    pattern: '/api/meals/:id/entries',
    conflict: 'append',
    creates: 'day-entry',
    idempotence: '`clientId` unique sur `log_entry`.',
  },
  {
    method: 'POST',
    pattern: '/api/meals/:id/copy',
    conflict: 'append',
    idempotence:
      "`clientId` décliné par entrée source (`<clientId>#<id>`) : une copie interrompue au milieu se rejoue sans doubler une seule ligne.",
  },
  {
    method: 'PATCH',
    pattern: '/api/entries/:id',
    conflict: 'last-write-wins',
    idempotence: 'Valeurs absolues ; le serveur recalcule les macros (§5.1).',
  },
  {
    method: 'DELETE',
    pattern: '/api/entries/:id',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },
  {
    method: 'PATCH',
    pattern: '/api/entries/:id/ingredients',
    conflict: 'last-write-wins',
    idempotence:
      'Quantités absolues sur des `log_entry_ingredient.id` ; le total parent est recalculé depuis les lignes.',
  },

  /* --- Modèles de journée et planning (étape 5) ------------------------- */
  {
    method: 'POST',
    pattern: '/api/templates',
    conflict: 'append',
    creates: 'response-id',
    idempotence: '`clientId` unique sur `day_template`.',
  },
  {
    method: 'PUT',
    pattern: '/api/templates/:id',
    conflict: 'last-write-wins',
    idempotence: 'Remplacement en bloc, adressé par la ressource.',
  },
  {
    method: 'DELETE',
    pattern: '/api/templates/:id',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },
  {
    method: 'PUT',
    pattern: '/api/planning/weekly/:weekday',
    conflict: 'last-write-wins',
    idempotence: 'Le jour de semaine **est** la clé (0 = lundi).',
  },
  {
    method: 'DELETE',
    pattern: '/api/planning/weekly/:weekday',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },
  {
    method: 'PUT',
    pattern: '/api/planning/override/:date',
    conflict: 'last-write-wins',
    idempotence: 'La date **est** la clé.',
  },
  {
    method: 'DELETE',
    pattern: '/api/planning/override/:date',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },

  /* --- Aliments (étape 6) ----------------------------------------------- */
  {
    method: 'POST',
    pattern: '/api/foods',
    conflict: 'append',
    creates: 'response-id',
    idempotence:
      '`clientId` unique, **et** clé naturelle `offCode` pour un import : rejouer renvoie la fiche existante, valeurs comprises (specs §7.2).',
  },
  {
    method: 'PATCH',
    pattern: '/api/foods/:id',
    conflict: 'last-write-wins',
    idempotence:
      'Valeurs absolues ; portions **remplacées en bloc**, jamais ajoutées — rejouer réécrit la même liste.',
  },
  {
    method: 'DELETE',
    pattern: '/api/foods/:id',
    conflict: 'delete-wins',
    idempotence:
      "Idempotent sur l'état. ⚠️ Peut répondre **409 `food_in_use`** — un refus applicatif définitif, que la file doit présenter et non retenter (§3.3).",
  },

  /* --- Recettes (étape 7) ----------------------------------------------- */
  {
    method: 'POST',
    pattern: '/api/recipes',
    conflict: 'append',
    creates: 'response-id',
    idempotence: '`clientId` unique sur `recipe`.',
  },
  {
    method: 'PATCH',
    pattern: '/api/recipes/:id',
    conflict: 'last-write-wins',
    idempotence: 'Valeurs absolues ; ingrédients et tags remplacés en bloc.',
  },
  {
    method: 'DELETE',
    pattern: '/api/recipes/:id',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },

  /* --- Poids (étape 8, corrigé à la V2 §2.2) ---------------------------- */
  {
    method: 'PUT',
    pattern: '/api/weight/:date',
    conflict: 'last-write-wins',
    idempotence:
      'La date **est** la clé primaire : saisir et corriger sont la même opération (specs §6.6).',
  },
  {
    method: 'DELETE',
    pattern: '/api/weight/:date',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },
  {
    method: 'POST',
    pattern: '/api/weight/goals',
    conflict: 'append',
    idempotence:
      '`clientId` unique sur `weight_goal` — **ajouté par cette étape** (V2 §2.2). Dédoublonne le rejeu, jamais les objectifs : deux gestes distincts écrivent toujours deux lignes.',
  },
  {
    method: 'DELETE',
    pattern: '/api/weight/goals/:id',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },

  /* --- Exercices (V2, étape 4) ------------------------------------------ */
  /*
   * 🔴 **Adressées par `clientId`, jamais par l'`id` entier** (V2 §2.3-b). C'est
   * ce qui rend ces trois routes composables hors ligne **sans un seul
   * place-titre** : le client connaît déjà l'identité de ce qu'il écrit, donc
   * il n'a pas à attendre que le serveur la lui attribue. Les routines de
   * l'étape 5 pointeront vers ces `clientId`, et c'est là que la propriété
   * paiera.
   *
   * ⚠️ **Inscrites dès maintenant, alors que le §3.1 ne promet que la
   * *recherche* hors ligne.** Le motif n'est pas l'anticipation : une route
   * clientId-adressée qui ne serait pas dans la file aurait payé le prix du
   * §2.3-b sans en toucher le bénéfice, et le trou ne se verrait qu'à
   * l'étape 5, au moment où une routine référencerait un exercice créé hors
   * ligne. C'est exactement le défaut que l'étape 3 a rencontré avec les
   * entrées loguées dans un repas créé hors ligne.
   */
  {
    method: 'PUT',
    pattern: '/api/exercises/:clientId',
    conflict: 'last-write-wins',
    idempotence:
      "Le `clientId` **est** l'adresse : créer, c'est écrire une ressource nommée. Rejouer réécrit la même ligne au même endroit, sans lecture préalable ni table de dédoublonnage (V2 §2.3-b).",
  },
  {
    method: 'PATCH',
    pattern: '/api/exercises/:clientId',
    conflict: 'last-write-wins',
    idempotence: 'Valeurs absolues, aucun incrément — le favori se pose, il ne se bascule pas.',
  },
  {
    method: 'DELETE',
    pattern: '/api/exercises/:clientId',
    conflict: 'delete-wins',
    idempotence:
      "Idempotent sur l'état. ⚠️ Répond **409 `exercise_in_use`** depuis l'étape 5 (`routine_line.exercise_id` en `RESTRICT`) — un refus applicatif définitif, que la file doit présenter et non retenter (§3.3). Sa cause peut disparaître : retirer l'exercice de la routine rend le « Réessayer » de Réglages → Synchronisation utile, exactement comme pour `food_in_use`.",
  },

  /* --- Routines (V2, étape 5) ------------------------------------------- */
  /*
   * 🔴 **Adressées par `clientId` à tous les étages** (V2 §2.3-b) : la routine,
   * ses blocs, ses lignes et les exercices visés. C'est ce qui rend ces trois
   * routes composables hors ligne **sans un seul place-titre** — et sans
   * `dependsOn` non plus, puisque l'arbre entier voyage dans **une** opération.
   * C'est la première fonction du projet à encaisser vraiment le §2.3-b : à
   * l'étape 3, composer un repas et ses entrées demandait des place-titres et
   * un ordre de drainage.
   */
  {
    method: 'PUT',
    pattern: '/api/routines/:clientId',
    conflict: 'last-write-wins',
    idempotence:
      "Le `clientId` **est** l'adresse : créer, c'est écrire une ressource nommée. Blocs et lignes sont **remplacés en bloc** et portent eux aussi leur `clientId`, donc rejouer réécrit exactement le même arbre — aucune ligne n'est dupliquée, aucune position ne dérive (V2 §2.3-b).",
  },
  {
    method: 'PATCH',
    pattern: '/api/routines/:clientId',
    conflict: 'last-write-wins',
    idempotence:
      "Valeurs absolues, aucun incrément — le favori se pose, il ne se bascule pas. Ne touche jamais aux blocs.",
  },
  {
    method: 'DELETE',
    pattern: '/api/routines/:clientId',
    conflict: 'delete-wins',
    idempotence:
      "Idempotent sur l'état ; blocs et lignes partent par cascade. Aucun 409 possible : le `RESTRICT` protège l'**exercice** contre sa propre suppression, pas la routine qui le cite.",
  },

  /* --- Séances (V2, étape 6) -------------------------------------------- */
  /*
   * 🔴 **C'est ici que le §2.3-b paie vraiment.** Une séance dure 45 à 90
   * minutes, encaisse une trentaine de mutations et se déroule sur un réseau
   * structurellement mauvais (§1.2) : c'est le seul écran de l'application
   * pour lequel la file n'est pas une robustesse mais le **substrat**. La
   * séance, chacune de ses séries et chacune de ses notes portent leur
   * `clientId`, donc **aucune de ces six routes n'a besoin d'un place-titre
   * `{{op:n}}` ni d'un `dependsOn`** — et surtout, aucune n'a besoin que les
   * autres soient drainées avant elle.
   *
   * 🔴 **Que des `PUT` et des `DELETE`, jamais un `PATCH`, et c'est ce qui
   * rend l'ordre de drainage indifférent.** Un `PATCH` sur une série dont le
   * `PUT` n'a pas encore été drainé répondrait 404 — donc `failed` définitif
   * ci-dessous — c'est-à-dire **une série perdue en silence au milieu d'une
   * séance**, très exactement le défaut que l'étape 6 existe pour empêcher.
   * Chaque opération écrit ici une ressource nommée et complète.
   */
  {
    method: 'PUT',
    pattern: '/api/sessions/:clientId',
    conflict: 'last-write-wins',
    idempotence:
      "Le `clientId` **est** l'adresse : créer, c'est écrire une ressource nommée. ⚠️ Ce corps ne porte **pas** les séries — s'il les portait, son rejeu réécrirait le plan initial par-dessus des séries déjà faites, et effacerait la séance en la « réparant ».",
  },
  {
    method: 'DELETE',
    pattern: '/api/sessions/:clientId',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état ; séries et notes partent par cascade.",
  },
  {
    method: 'PUT',
    pattern: '/api/sessions/:clientId/sets/:setClientId',
    conflict: 'last-write-wins',
    idempotence:
      "La série entière voyage, adressée par son `clientId` : rejouer réécrit la même ligne au même endroit. La même écriture pose le plan **et** enregistre le résultat, donc une validation dont le plan n'a jamais été transmis l'écrit simplement pour la première fois.",
  },
  {
    method: 'DELETE',
    pattern: '/api/sessions/:clientId/sets/:setClientId',
    conflict: 'delete-wins',
    idempotence:
      "Idempotent sur l'état. ⚠️ Ce n'est **pas** le chemin normal : retirer une série d'une séance en cours, c'est `status: 'skipped'` (§4.5) — on veut savoir qu'elle était prévue.",
  },
  {
    method: 'PUT',
    pattern: '/api/sessions/:clientId/plan',
    /*
     * 🔴 **`append`, et non `last-write-wins`** : cette route n'écrase rien —
     * elle insère les séries manquantes et laisse intactes celles qui
     * existent. Il n'y a donc rien à arbitrer, et elle ne porte pas
     * d'`emittedAt`.
     *
     * ⚠️ C'est ce qui la rend sûre là où un plan porté par le `PUT` de la
     * séance ne l'était pas : rejouée trois heures plus tard par la file, elle
     * ne peut pas repasser en `pending` une série entre-temps validée.
     */
    conflict: 'append',
    idempotence:
      "Insertion **conditionnelle** : chaque série porte son `clientId`, et la route ne touche pas à celles qui existent déjà. Rejouer n'insère rien — l'idempotence est structurelle, pas arbitrée.",
  },
  {
    method: 'PUT',
    pattern: '/api/sessions/:clientId/notes/:exerciseClientId',
    conflict: 'last-write-wins',
    idempotence:
      "La paire (séance, exercice) **est** la clé primaire, donc la clé d'idempotence naturelle — même raisonnement que `PUT /api/weight/:date`. Le corps porte la valeur voulue, jamais un ajout au texte existant.",
  },
  {
    method: 'DELETE',
    pattern: '/api/sessions/:clientId/notes/:exerciseClientId',
    conflict: 'delete-wins',
    idempotence: "Idempotent sur l'état.",
  },

  /* --- Notifications : les réglages seulement (étapes 9 et 11) ---------- */
  {
    method: 'PUT',
    pattern: '/api/notifications/:type',
    conflict: 'last-write-wins',
    idempotence: "Le type **est** la clé ; deux champs, tous deux tenus par l'écran.",
  },
  {
    method: 'PUT',
    pattern: '/api/notifications/timezone',
    conflict: 'last-write-wins',
    idempotence: 'Réglage unique, valeur absolue.',
  },
];

/* ==========================================================================
 * Les refus, et leur motif
 * ======================================================================= */

/**
 * Ce qui **n'entre jamais** dans la file, et échoue franchement hors ligne.
 *
 * Cette liste n'est pas nécessaire au fonctionnement — tout ce qui n'est pas
 * dans la liste blanche est déjà refusé. Elle existe pour que le refus soit
 * **motivé et lisible** : sans elle, on ne distingue pas une route délibérément
 * écartée d'une route qu'on a oublié d'inscrire. C'est aussi ce qui permet à un
 * écran d'expliquer *pourquoi* un bouton est inerte hors ligne, plutôt que
 * d'afficher un échec brut.
 */
export const NON_SYNCABLE_MUTATIONS: readonly NonSyncableRoute[] = [
  {
    method: '*',
    pattern: '/api/auth/*',
    reason:
      "Cérémonie WebAuthn : un défi ne sert qu'une fois, elle n'est pas rejouable par nature. Décision explicite de l'étape 4, hors règle n°9.",
  },
  {
    method: '*',
    pattern: '/api/off/*',
    reason:
      "Dépend d'un tiers (§5.2). Rien à mettre en attente : sans réseau il n'y a pas d'Open Food Facts, et la moitié personnelle du résultat s'affiche déjà normalement (`offStatus: \"unavailable\"`, étape 6).",
  },
  {
    method: 'POST',
    pattern: '/api/notifications/test',
    reason:
      "C'est une **action**, pas une mutation d'état durable : elle *envoie* un push. Mise en file, elle partirait trois heures plus tard sans que personne ne l'ait demandé.",
  },
  {
    method: 'POST',
    pattern: '/api/notifications/subscription',
    reason:
      "Décrit l'état **présent** du navigateur. Rejouée plus tard, elle déclarerait un abonnement peut-être déjà révoqué.",
  },
  {
    method: 'DELETE',
    pattern: '/api/notifications/subscription',
    reason: "Même raison que sa création : l'abonnement décrit le présent, pas une intention.",
  },
  {
    method: 'POST',
    pattern: '/api/intervals/sync',
    reason:
      "C'est une **action**, pas une mutation d'état durable — exactement `POST /api/notifications/test` (V2 §2.4). Elle *déclenche* une ingestion depuis un tiers ; rejouée le lendemain, elle n'aurait plus d'objet, le tick de fond s'en étant chargé quarante-huit fois. Hors ligne elle échoue franchement, ce qui est juste : il n'y a rien à importer d'un tiers sans réseau.",
  },
  {
    method: 'PUT',
    pattern: '/api/intervals/key',
    reason:
      "🔴 **Un secret ne se met pas en file.** Techniquement elle serait rejouable sans dommage — c'est un `PUT` sur une ressource nommée, donc idempotent par construction. Ce qui l'exclut n'est pas l'idempotence mais le **stockage** : une opération en attente dort en clair dans IndexedDB, côté navigateur, jusqu'au retour du réseau. La clé d'API n'a rien à y faire. C'est le seul refus de cette liste qui soit motivé par la nature de la *donnée* et non par celle de l'*opération*.",
  },
];

/* ==========================================================================
 * Correspondance
 * ======================================================================= */

/**
 * Contraintes par nom de segment dynamique.
 *
 * ⚠️ **Elles ne sont pas décoratives.** Sans elles, `PUT /api/notifications/:type`
 * capterait `/api/notifications/timezone`, et `DELETE /api/weight/:date`
 * capterait `/api/weight/goals`. Ici les deux collisions seraient sans gravité
 * (les quatre routes sont éligibles), mais une liste blanche qui reconnaît une
 * route **pour la mauvaise raison** finit tôt ou tard par en reconnaître une
 * qu'elle devait refuser.
 */
const SEGMENT_RULES: Record<string, RegExp> = {
  ':id': /^[1-9][0-9]*$/,
  ':date': /^\d{4}-\d{2}-\d{2}$/,
  ':weekday': /^[0-6]$/,
  ':type': /^(?:weigh_in|empty_log|daily_summary)$/,
  /**
   * Identité **cliente** d'une ressource V2 (§2.3-b) — un UUID en pratique.
   *
   * ⚠️ **Volontairement plus large qu'un motif d'UUID.** Contraindre à
   * `xxxxxxxx-xxxx-…` supposerait que toute identité future soit tirée par
   * `crypto.randomUUID()`, alors que le §2.3-b n'exige qu'une chose : que le
   * client la possède. Une identité dérivée d'une autre source — c'est le cas
   * des exercices importés, dont le `clientId` vient du jeu de données —
   * sortirait de la file sans que rien ne le dise. C'est très exactement le
   * défaut de l'étape 3 : la contrainte `:id` refusait les identifiants
   * provisoires négatifs, et les entrées loguées dans un repas créé hors ligne
   * **sortaient de la file**.
   *
   * ⚠️ Reste borné, et c'est ce qui compte pour une liste blanche : jeu de
   * caractères sûr comme segment d'URL, longueur plafonnée. Un segment qui
   * n'est pas une identité (`import`, `facettes`) ne peut pas être capté par
   * accident **parce qu'aucune route littérale de ce genre n'est déclarée** —
   * et si une l'était, elle serait déclarée avant, littéralement.
   */
  ':clientId': /^[0-9A-Za-z][0-9A-Za-z._-]{0,127}$/,
  /**
   * Les identités clientes **imbriquées** de la séance (étape 6) — la série
   * dans sa séance, l'exercice dans sa note.
   *
   * 🔴 **Déclarées séparément alors qu'elles portent le même motif, parce que
   * `matchesPattern` refuse tout segment dynamique sans règle.** C'est une
   * propriété de la liste blanche, pas une lourdeur : sans ces deux lignes,
   * `PUT /api/sessions/:c/sets/:sc` **sortirait de la file** en silence, et
   * une séance saisie hors ligne partirait sans ses séries. C'est exactement
   * le défaut de l'étape 3 — les entrées loguées dans un repas créé hors ligne
   * sortaient de la file parce que la contrainte `:id` refusait les
   * identifiants provisoires négatifs — reproduit une marche plus haut.
   */
  ':setClientId': /^[0-9A-Za-z][0-9A-Za-z._-]{0,127}$/,
  ':exerciseClientId': /^[0-9A-Za-z][0-9A-Za-z._-]{0,127}$/,
};

/** Retire la requête et le fragment : `/api/weight/goals?range=30d` → `/api/weight/goals`. */
function pathOnly(path: string): string {
  const cut = path.search(/[?#]/);
  return cut === -1 ? path : path.slice(0, cut);
}

function segmentsOf(path: string): string[] {
  return pathOnly(path)
    .split('/')
    .filter((part) => part !== '');
}

/** Le chemin correspond-il au motif ? Un `*` final couvre tout ce qui suit. */
function matchesPattern(pattern: string, path: string): boolean {
  const wanted = segmentsOf(pattern);
  const actual = segmentsOf(path);

  if (wanted.at(-1) === '*') {
    const prefix = wanted.slice(0, -1);
    if (actual.length < prefix.length) return false;
    return prefix.every((segment, index) => segment === actual[index]);
  }

  if (wanted.length !== actual.length) return false;

  return wanted.every((segment, index) => {
    const value = actual[index] ?? '';
    const rule = SEGMENT_RULES[segment];
    if (rule) return rule.test(value);
    // Un segment dynamique sans contrainte déclarée n'existe pas : ce serait un
    // trou dans la liste blanche, pas une souplesse. On refuse.
    if (segment.startsWith(':')) return false;
    return segment === value;
  });
}

/**
 * Cette mutation est-elle éligible à la file ?
 *
 * 🔴 **Refuse tout ce qu'elle ne reconnaît pas** — méthode inconnue, `GET`,
 * route absente de la liste. C'est la propriété qui compte : une fonction
 * future n'hérite de la file qu'en s'y inscrivant (V2 §11).
 */
export function isSyncable(method: string, path: string): boolean {
  return syncableRouteFor(method, path) !== null;
}

/**
 * La déclaration de cette mutation, ou `null` si elle n'est pas éligible.
 *
 * ⚠️ **Un seul point de correspondance pour tout le monde.** `isSyncable`,
 * la politique de conflit et la source d'identité en dérivent toutes, si bien
 * qu'une route ne peut pas être « éligible ici et pas là ». Le serveur consomme
 * cette même fonction pour arbitrer les écritures concurrentes : la politique
 * du §3.4 est donc lue au même endroit des deux côtés de l'API, ce qui est la
 * seule façon qu'elle ne diverge pas.
 */
export function syncableRouteFor(method: string, path: string): SyncableRoute | null {
  const verb = method.toUpperCase();
  return (
    SYNCABLE_MUTATIONS.find(
      (route) => route.method === verb && matchesPattern(route.pattern, path),
    ) ?? null
  );
}

/**
 * Politique de conflit d'une mutation — `null` si elle n'est pas éligible.
 *
 * ⚠️ Un `null` ne veut pas dire « ajout » : il veut dire « hors file ». Une
 * route hors file n'a pas de politique parce qu'elle ne sera jamais rejouée.
 */
export function conflictPolicyOf(method: string, path: string): ConflictPolicy | null {
  return syncableRouteFor(method, path)?.conflict ?? null;
}

/**
 * Cette écriture doit-elle être **arbitrée par l'horodatage d'émission** ?
 *
 * 🔴 C'est le prédicat que le serveur consulte (`services/writeStamp.ts`) pour
 * décider si une requête porte un `emittedAt` et si une écriture plus ancienne
 * doit être ignorée. Le lire ici plutôt que de le redéclarer côté serveur est
 * ce qui garantit qu'ajouter une route à la file la soumette du même coup à la
 * politique de conflit — au lieu de l'oublier.
 */
export function isLastWriteWins(method: string, path: string): boolean {
  return conflictPolicyOf(method, path) === 'last-write-wins';
}

/**
 * Où lire l'`id` créé par cette mutation — `null` si elle n'en produit pas
 * d'exact, ou si elle n'est pas éligible.
 */
export function createdIdSourceOf(method: string, path: string): CreatedIdSource | null {
  return syncableRouteFor(method, path)?.creates ?? null;
}

/**
 * Motif du refus, quand il a été rédigé — `null` sinon.
 *
 * ⚠️ Un `null` ne veut **pas** dire « éligible » : il veut dire « aucun motif
 * rédigé ». Toujours interroger `isSyncable()` pour décider ; ceci ne sert qu'à
 * expliquer.
 */
export function syncExclusionReason(method: string, path: string): string | null {
  const verb = method.toUpperCase();
  const found = NON_SYNCABLE_MUTATIONS.find(
    (route) =>
      (route.method === '*' || route.method === verb) && matchesPattern(route.pattern, path),
  );
  return found ? found.reason : null;
}
